import { Injectable } from '@angular/core';
import {
  auditTime,
  BehaviorSubject,
  distinctUntilChanged,
  fromEvent,
  map,
  startWith,
  tap,
} from 'rxjs';
import { breakPoints, emptyScreenSize } from '@core/services/constants';
import { ScreenSize } from '@core/models';
import * as utils from '@core/services/utils/utility-service.utils';

@Injectable({ providedIn: 'root' })
export class ScreenSizeService {
  private screenSizeSubject$ = new BehaviorSubject<ScreenSize>(emptyScreenSize);
  readonly screenSize$ = this.screenSizeSubject$.asObservable();

  screenSize: ScreenSize;

  //* Individual Size Helpers
  readonly isDesktop$ = this.screenSize$.pipe(map(utils.isDesktop));
  readonly isLaptop$ = this.screenSize$.pipe(map(utils.isLaptop));
  readonly isMobile$ = this.screenSize$.pipe(map(utils.isMobile));
  readonly isTablet$ = this.screenSize$.pipe(map(utils.isTablet));
  readonly isXLarge$ = this.screenSize$.pipe(map(utils.isXLarge));

  //* Grouped Size Helpers
  readonly isAllSizesExceptMobile$ = this.screenSize$.pipe(map(utils.isAllSizesExceptMobile));
  readonly isLargeScreens$ = this.screenSize$.pipe(map(utils.isLargeScreens));
  readonly isSmallScreens$ = this.screenSize$.pipe(map(utils.isSmallScreens));

  constructor() {
    this.getScreenSize();
  }

  getScreenSize(): void {
    fromEvent(window, 'resize')
      .pipe(
        //* Slow down time to rerender
        auditTime(100),
        startWith({ target: window }),
        map(() => this.buildScreenSize(window.innerWidth)),
        distinctUntilChanged()
      )
      .subscribe(screenSize => this.screenSizeSubject$.next(screenSize));
  }

  buildScreenSize(windowWidth: number): ScreenSize {
    if (windowWidth > 300) {
      const isRange = (num: number, range: number[]) => num > range[0] && num < range[1];
      const filterSize = (breakPoints: [string, number[]]) => isRange(windowWidth, breakPoints[1]);
      const newScreenSize = Object.entries(breakPoints)?.filter(filterSize)[0][0];
      if (newScreenSize) {
        this.screenSize = { ...emptyScreenSize, [`is${newScreenSize}`]: true };
        return { ...emptyScreenSize, [`is${newScreenSize}`]: true };
      } else {
        return this.screenSize;
      }
    } else return { ...emptyScreenSize, isMobile: true };
  }
}
